import { useState } from 'react'
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { FilePicker } from '../../Common/FilePicker'
import { Button } from '../../Common/Button'
import { extractSpreadsheetText, extractDocumentText } from '../../../utils/fileExtractor'
import { useNls } from '../../../nls/nlsProvider'
import type { DataContext } from '../../../types/aiChatSource'

export interface NewFileIngestPaneProps {
  onAdd: (context: DataContext) => void
}

type ParseState =
  | { status: 'idle' }
  | { status: 'parsing'; fileName: string }
  | { status: 'done'; context: DataContext }
  | { status: 'error'; message: string }

const isSpreadsheet = (file: File) => {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  return ['xlsx', 'xls', 'csv'].includes(ext)
}

export const NewFileIngestPane = ({ onAdd }: NewFileIngestPaneProps) => {
  const { t } = useNls()
  const [parseState, setParseState] = useState<ParseState>({ status: 'idle' })

  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return
    const file = files[0]
    setParseState({ status: 'parsing', fileName: file.name })

    try {
      let text: string
      let kind: 'spreadsheet' | 'document'
      if (isSpreadsheet(file)) {
        text = await extractSpreadsheetText(file)
        kind = 'spreadsheet'
      } else {
        text = await extractDocumentText(file)
        kind = 'document'
      }

      const context: DataContext = {
        id: `ingest-${kind}:${file.name}-${Date.now()}`,
        kind,
        label: file.name,
        sizeBytes: file.size,
        text,
        fixedIngestCr: 5,
      }
      setParseState({ status: 'done', context })
    } catch (err) {
      setParseState({
        status: 'error',
        message: err instanceof Error ? err.message : t('NLSAiContextPickerParseFailedMsg'),
      })
    }
  }

  const handleAttach = () => {
    if (parseState.status !== 'done') return
    onAdd(parseState.context)
    handleReset()
  }

  const handleReset = () => {
    setParseState({ status: 'idle' })
  }

  return (
    <div className="ctxPane ctxPane--newFile">
      <div className="ctxPane__ingestHero">
        <NoteAddRoundedIcon style={{ fontSize: 36, opacity: 0.5 }} />
        <h4>{t('NLSAiContextPickerCategoryNewFile')}</h4>
        <p>Upload an Excel, CSV, Word, PDF, TXT, or Markdown file to parse directly into AI context.</p>
        <p className="ctxPane__costNote">+5 Cr fixed parse fee per file</p>
      </div>

      {parseState.status === 'idle' && (
        <FilePicker
          accept=".xlsx,.xls,.csv,.docx,.pdf,.txt,.md"
          multiple={false}
          maxBytes={Number.MAX_SAFE_INTEGER}
          onFiles={handleFiles}
        />
      )}

      {parseState.status === 'parsing' && (
        <div className="ctxPane__parseStatus ctxPane__parseStatus--busy">
          <div className="ctxPane__spinner" />
          <span>{t('NLSAiContextPickerParsingFile', { fileName: parseState.fileName })}</span>
        </div>
      )}

      {parseState.status === 'done' && (
        <div className="ctxPane__parseStatus ctxPane__parseStatus--done">
          <CheckCircleRoundedIcon style={{ color: 'var(--success)' }} />
          <div className="ctxPane__parseInfo">
            <strong>{parseState.context.label}</strong>
            <small>
              {parseState.context.sizeBytes
                ? `${(parseState.context.sizeBytes / 1024).toFixed(1)} KB · `
                : ''}
              {parseState.context.kind} · {parseState.context.text.length.toLocaleString()} chars extracted
            </small>
          </div>
          <div className="ctxPane__parseActions">
            <Button size="sm" variant="primary" onClick={handleAttach}>
              {t('NLSAiContextPickerAttach')}
            </Button>
            <Button size="sm" variant="tertiary" onClick={handleReset}>
              {t('NLSAiContextPickerClear')}
            </Button>
          </div>
        </div>
      )}

      {parseState.status === 'error' && (
        <div className="ctxPane__parseStatus ctxPane__parseStatus--error">
          <ErrorOutlineRoundedIcon style={{ color: 'var(--danger)' }} />
          <div className="ctxPane__parseInfo">
            <strong>{t('NLSAiContextPickerParseFailed')}</strong>
            <small>{parseState.message}</small>
          </div>
          <Button size="sm" variant="tertiary" onClick={handleReset}>
            {t('NLSAiContextPickerTryAgain')}
          </Button>
        </div>
      )}
    </div>
  )
}
